import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type RefObject,
} from "react";

type DiscardPrompt = (proceed: () => void, cancel?: () => void) => void;
const forms = new Map<string, DiscardPrompt>();
const listeners = new Set<() => void>();
const notify = (): void => listeners.forEach((listener) => listener());
export const subscribeDirtyForms = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};
export const hasDirtyForms = (): boolean => forms.size > 0;
export const confirmFormNavigation = (
    proceed: () => void,
    cancel?: () => void
): void => {
    const prompt = Array.from(forms.values()).at(-1);
    if (prompt) {
        prompt(proceed, cancel);
    } else {
        proceed();
    }
};

const snapshot = (root: HTMLElement): string =>
    JSON.stringify(
        Array.from(
            root.querySelectorAll<HTMLElement>(
                "input:not([type=search]):not([data-transient]),textarea,select,[role=switch],[data-form-value],[contenteditable=true]"
            )
        ).map((element) => {
            if (element instanceof HTMLInputElement) {
                return [
                    element.name || element.id,
                    element.type === "checkbox" || element.type === "radio"
                        ? element.checked
                        : element.value,
                ];
            }
            if (
                element instanceof HTMLTextAreaElement ||
                element instanceof HTMLSelectElement
            ) {
                return [element.name || element.id, element.value];
            }
            return (
                element.dataset.formValue ??
                element.getAttribute("aria-checked") ??
                element.textContent
            );
        })
    );

export const useFormProtection = (
    id: string,
    open: boolean,
    enabled: boolean,
    ref: RefObject<HTMLDivElement | null>,
    explicitDirty?: boolean
) => {
    const baseline = useRef<string | null>(null);
    const started = useRef(false);
    const [detectedDirty, setDirty] = useState(false);
    const [pending, setPending] = useState<{
        proceed: () => void;
        cancel?: () => void;
    } | null>(null);
    const dirty = enabled && (explicitDirty ?? detectedDirty);
    const capture = (): void => {
        if (!enabled || !ref.current) {
            return;
        }
        baseline.current ??= snapshot(ref.current);
        started.current = true;
        setDirty(snapshot(ref.current) !== baseline.current);
        requestAnimationFrame(() => {
            if (ref.current) {
                setDirty(snapshot(ref.current) !== baseline.current);
            }
        });
    };
    const requestDiscard = useCallback<DiscardPrompt>(
        (proceed, cancel) => setPending({ proceed, cancel }),
        []
    );
    useLayoutEffect(() => {
        if (!open) {
            baseline.current = null;
            started.current = false;
            setDirty(false);
            setPending(null);
            return;
        }
        if (ref.current) {
            baseline.current = snapshot(ref.current);
        }
        const frame = requestAnimationFrame(() => {
            if (!started.current && ref.current) {
                baseline.current = snapshot(ref.current);
            }
        });
        return () => cancelAnimationFrame(frame);
    }, [open, ref]);
    useEffect(() => {
        if (!open || !dirty) {
            return;
        }
        forms.set(id, requestDiscard);
        notify();
        const beforeUnload = (event: BeforeUnloadEvent): void => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", beforeUnload);
        return () => {
            forms.delete(id);
            notify();
            window.removeEventListener("beforeunload", beforeUnload);
        };
    }, [open, dirty, id, requestDiscard]);
    const close = (action: () => void): void => {
        if (dirty) {
            requestDiscard(action);
        } else {
            action();
        }
    };
    const discard = (): void => {
        const action = pending?.proceed;
        setPending(null);
        baseline.current = null;
        setDirty(false);
        forms.delete(id);
        notify();
        action?.();
    };
    const cancel = (): void => {
        pending?.cancel?.();
        setPending(null);
    };
    return {
        capture,
        close,
        discard,
        cancel,
        pending: Boolean(pending),
        dirty,
    };
};
